import React, { useEffect, useState } from 'react'
// import moment from 'moment'
import numeral from 'numeral'
import { get, orderBy } from 'lodash'
import Appbar from 'components/Appbar'
import { Menu, MenuItem, Position } from '@blueprintjs/core'
import api, { Order, ICostBuilding } from '@api'
import { Button, LinkButton, Card, CitySelect, IconButton, YearSelect, PageContent } from '@ui'
import track from '@track'
import useCost, { ICost } from './hooks/useCost'
import Chart from './Chart'
import CostTable from './Table'
import ImportFlow from '../ImportFlow'
import { useKeynav, useFileDownload, useUser } from '@hooks'
import useImport, { Import } from 'components/ImportFlow/hooks/useImport'
import css from './cost.module.scss'
import { Popover } from '@ui'
import { DateUtil } from '@utils'
import { useHRLocations } from '@ui/selects/HRLocationSelect'
import LocationFilterNote from 'components/EmployeeManagement/LocationFilterNote'
import { legacyMode } from 'App'
const currentYear = DateUtil().year()

export interface ITab {
  dataKey: string
  title: string
  render: (cost: ICost) => JSX.Element
  format: string
}

export default function () {
  const cost = useCost(currentYear)
  const keynav = useKeynav(tabs.map((t) => t.dataKey))
  const tab: ITab = tabs.find((t) => t.dataKey === keynav.val) as ITab
  const buildings: ICostBuilding[] = orderBy(
    Object.values(cost.buildings),
    cost.sort.column,
    cost.sort.order === Order.ASC ? 'asc' : 'desc'
  ) as ICostBuilding[]

  const importCost = useImport(Import.OP_COST)

  const startfileDownload = useFileDownload()

  const [importing, setImporting] = useState(false)

  useEffect(() => {
    if (!cost.exportList) return
    startfileDownload(cost.exportList)
  }, [cost.exportList, startfileDownload])

  const dlTemplate = (blob: string) => {
    const a = document.createElement('a')
    a.download = 'opcost_template.csv'
    a.target = '_blank'
    a.href = window.URL.createObjectURL(new Blob([blob], { type: 'text/plain' }))
    a.click()
  }
  const locations = useHRLocations()
  const user = useUser()
  const preferredLocation = get(user, 'user.employee.preferredLocations', [])
  const locationFilterNote =
    !legacyMode &&
    (preferredLocation.length === 0 || locations.length === preferredLocation.length ? null : (
      <LocationFilterNote />
    ))
  return (
    <>
      <Appbar title="Cost" locationFilterNote={locationFilterNote} />
      <PageContent>
        <Card>
          <Card.Header style={{ width: '100%' }}>
            <div className={css.city_years_wrapper}>
              <CitySelect
                useAll
                includeOldBuildings
                value={cost.filters.city}
                onChange={(city: string) => {
                  cost.filterCity(city)
                  track.cost({
                    eventName: track.Event.FILTER,
                    props: {
                      filterName: 'City',
                      filterValue: city,
                    },
                  })
                }}
                buttonProps={{ iconName: 'map-marker-alt' }}
              />
              <YearSelect
                className="ml2"
                onChange={(year) => {
                  cost.setYear(year)
                  track.cost({
                    eventName: track.Event.FILTER,
                    props: {
                      filterName: 'Year',
                      filterValue: year,
                    },
                  })
                }}
                options={cost.yearOptions}
                value={cost.year}
              />
            </div>
            <div>
              <Button
                text="Import"
                iconName="file-import"
                data-button="import_cost"
                onClick={() => {
                  track.cost({
                    eventName: track.Event.IMPORT,
                  })
                  setImporting(true)
                }}
              />
              {importing && (
                <ImportFlow
                  importHook={importCost}
                  title="Import Operation Costs"
                  onClose={() => setImporting(false)}
                  onComplete={cost.reload}>
                  <>
                    <p>
                      <LinkButton
                        onClick={() => api.cost.template().then(dlTemplate)}
                        text="Download the .csv template"
                      />{' '}
                      for bulk operation cost import
                    </p>
                    <ul>
                      <li>
                        You can import operation cost for multiple buildings, or a single building.
                      </li>
                      <li>New data will merge with existing data.</li>
                    </ul>
                  </>
                </ImportFlow>
              )}
              <Popover
                minimal
                position={Position.BOTTOM}
                content={
                  <Menu>
                    <MenuItem
                      text="Download"
                      onClick={() => {
                        cost.download()
                        track.cost({
                          eventName: track.Event.EXPORT,
                        })
                      }}
                    />
                  </Menu>
                }>
                <IconButton
                  round={false}
                  className="ml2"
                  iconName="ellipsis-h"
                  tooltip="More actions"
                  data-button="more_actions"
                />
              </Popover>
            </div>
          </Card.Header>
          <Chart
            {...{
              buildings,
              cost,
              tabId: keynav.val,
              setTabId: (tab) => keynav.setVal(tab),
            }}
          />
          <CostTable {...{ buildings, cost, tab }} />
        </Card>
      </PageContent>
    </>
  )
}

export const tabs: ITab[] = [
  {
    title: 'Total Annual Cost',
    render: (cost) => (
      <div className={css.tab_style}>
        <h1>{numeral(cost.annualTotal).format('$0,0.0a')}</h1>
        <h3>Total Annual Cost</h3>
      </div>
    ),
    dataKey: 'costs.annualTotal',
    format: '$0,0.0a',
  },
  {
    title: 'Avg Cost/Sqft',
    render: (cost) => (
      <div className={css.tab_style}>
        <h1>{numeral(cost.annualCostPerSqft).format('$0,0.0a')}</h1>
        <h3>Avg Cost/Sqft</h3>
      </div>
    ),
    dataKey: 'costs.annualCostPerSqft',
    format: '$0,0.00a',
  },
  {
    title: 'Avg Cost/Seat',
    render: (cost) => (
      <div className={css.tab_style}>
        <h1>{numeral(cost.annualCostPerSeat).format('$0,0.0a')}</h1>
        <h3>Avg Cost/Seat</h3>
      </div>
    ),
    dataKey: 'costs.annualCostPerSeat',
    format: '$0,0.00a',
  },
  {
    title: 'Avg Cost/Employee',
    render: (cost) => (
      <div className={css.tab_style}>
        <h1>{numeral(cost.annualCostPerEmployee).format('$0,0.0a')}</h1>
        <h3>Avg Cost/Employee</h3>
      </div>
    ),
    dataKey: 'costs.annualCostPerEmployee',
    format: '$0,0.00a',
  },
]
